// @ts-nocheck
import { get, post, patch, put, deleteRequest } from './utils/api/base';
const API_URL = process.env.REACT_APP_BASE_API_URL || 'http://localhost:3500';
/**
 * ============================
 *       TIPAGENS DEPOTS
 * ============================
 */
/**
 * @typedef {Object} Depot
 * @property {number} Id
 * @property {string} ReferenciaCLiente
 * @property {string} NumeroContainer
 * @property {string} TipoContainer
 * @property {string} NumeroEmbarcador
 * @property {string} LocalColeta
 * @property {string} DataColeta
 * @property {string} StatusPedido
 * @property {string} DataEntrega
 * @property {string} Local
 * @property {string} LocalDevolucao
 * @property {string} DataDevolucao
 * @property {string} FreeTime
 * @property {string} MotDepot
 * @property {number} IdMotoristaColeta
 * @property {number} SituacaoDepot
 * @property {number} IdVeiculoColeta
 * @property {number} IdMotoristaEntrega
 * @property {number} IdVeiculoEntrega
 * @property {string} AnotacoesProgramacao
 * @property {number} StatusCTNR
 * @property {string|null} AnotacoesOperacionais
 * @property {string} StatusDepot
 * @property {{ Id:number, Nome:string, Cpf:string, CodigoIntegracao:string }} [MotoristaColeta]
 * @property {{ Id:number, Nome:string, Cpf:string, CodigoIntegracao:string }} [MotoristaEntrega]
 * @property {{ Id:number, Frota:string, Placa:string, Uf?:string|null, Renavam?:string|null, IdModeloVeicular?:number }} [VeiculoColeta]
 * @property {{ Id:number, Frota:string, Placa:string, Uf?:string|null, Renavam?:string|null, IdModeloVeicular?:number }} [VeiculoEntrega]
 * @property {string} [Alerta]
 */
/**
 * Filtros da listagem de Depots
 * (mesmos IDs usados na tela / hook useDepotsListagem)
 *
 * @typedef {Object} DepotFilters
 * @property {string} [Search]
 * @property {string} [ReferenciaCLiente]
 * @property {string} [NumeroContainer]
 * @property {string} [TipoContainer]
 * @property {string} [NumeroEmbarcador]
 * @property {string} [StatusPedido]
 * @property {string} [StatusDepot]
 * @property {number|string} [IdMotoristaColeta]
 * @property {number|string} [IdVeiculoColeta]
 * @property {number|string} [IdMotoristaEntrega]
 * @property {number|string} [IdVeiculoEntrega]
 * @property {number|string} [SituacaoDepot]
 * @property {number|string} [StatusCTNR]
 * @property {string} [DataColeta]
 * @property {string} [DataEntrega]
 * @property {string} [DataDevolucao]
 * @property {string} [FreeTime]
 * @property {number} [quantidadePorPagina]
 * @property {number} [pagina]
 */
/**
 * @typedef {Object} DepotsResponse
 * @property {Depot[]} registros
 * @property {number} totalRegistros
 * @property {number} paginaAtual
 * @property {number} quantidadePorPagina
 */
/**
 * Payload para criar/atualizar depot
 * (normalmente só os campos raiz, sem os objetos Motorista ou Veículo)
 *
 * @typedef {Object} DepotPayload
 * @property {string} [ReferenciaCLiente]
 * @property {string} [NumeroContainer]
 * @property {string} [TipoContainer]
 * @property {string} [NumeroEmbarcador]
 * @property {string} [LocalColeta]
 * @property {string} [DataColeta]
 * @property {string} [StatusPedido]
 * @property {string} [DataEntrega]
 * @property {string} [Local]
 * @property {string} [LocalDevolucao]
 * @property {string} [DataDevolucao]
 * @property {string} [FreeTime]
 * @property {string} [MotDepot]
 * @property {number} [IdMotoristaColeta]
 * @property {number} [SituacaoDepot]
 * @property {number} [IdVeiculoColeta]
 * @property {number} [IdMotoristaEntrega]
 * @property {number} [IdVeiculoEntrega]
 * @property {string} [AnotacoesProgramacao]
 * @property {number} [StatusCTNR]
 * @property {string|null} [AnotacoesOperacionais]
 * @property {string} [StatusDepot]
 * @property {string} [Alerta]
 */
/**
 * ============================
 *          SERVICES
 * ============================
 */
/**
 * Lista depots com filtros e paginação
 * @param {DepotFilters} [filters={}] - Filtros para a busca
 * @returns {Promise<DepotsResponse>}
 */
export const getDepots = async (filters = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, String(value));
        }
    });
    const url = `${API_URL}/depots?${queryParams.toString()}`;
    const response = await get(url);
    return response?.data || response || {};
};
/**
 * Obtém um depot por ID
 * @param {number|string} id - ID do depot
 * @returns {Promise<Depot>}
 */
export const getDepotsById = async (id) => {
    const response = await get(`${API_URL}/depots/${id}`);
    return response?.data || response;
};
/**
 * Cria um novo depot
 * @param {DepotPayload} payload
 * @returns {Promise<Depot>}
 */
export const createDepots = async (payload) => {
    const response = await post(`${API_URL}/depots`, payload);
    return response?.data || response;
};
/**
 * Atualiza um depot existente
 * @param {number|string} id - ID do depot
 * @param {DepotPayload} payload - Dados atualizados
 * @returns {Promise<Depot>}
 */
export const updateDepots = async (id, payload) => {
    const response = await put(`${API_URL}/depots/${id}`, payload);
    return response?.data || response;
};
/**
 * Remove um depot
 * @param {number|string} id
 * @returns {Promise<Object|void>}
 */
export const deleteDepots = async (id) => {
    const response = await deleteRequest(`${API_URL}/depots/${id}`);
    return response?.data || response;
};
/**
 * Gera e envia um pedido para o TMS a partir de um depot
 * @param {number|string} id - ID do depot
 * @param {Object} payload - Dados do pedido
 * @returns {Promise<Object>} Resposta da API
 */
export const gerarPedidoDepot = async (id, payload) => {
    const response = await post(`${API_URL}/depots/${id}/gerarPedido`, payload);
    return response?.data || response;
};
/**
 * Atualiza status de coleta/entrega/devolucao do depot
 * @param {number|string} id - ID do depot
 * @param {Object} payload - Dados de status
 * @returns {Promise<Object>}
 */
export const updateDepotStatus = async (id, payload) => {
    const response = await patch(`${API_URL}/depots/${id}/status`, payload);
    return response?.data || response;
};
/**
 * Adiciona um anexo a um depot
 * @param {number|string} id - ID do depot
 * @param {FormData} formData - Dados do arquivo
 * @returns {Promise<Object>} Anexo criado
 */
export const addAnexoDepot = async (id, formData, tagAnexo) => {
    const query = tagAnexo ? `?TagAnexo=${encodeURIComponent(tagAnexo)}` : '';
    const response = await post(`${API_URL}/depots/${id}/anexos${query}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response?.data || response;
};
export const getDepotsAnexos = async (filters = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, String(value));
        }
    });
    const url = `${API_URL}/depots/anexos?${queryParams.toString()}`;
    const response = await get(url);
    return response?.data || response || {};
};
export const downloadDepotsAnexosZip = async (ids = []) => {
    const payload = { ids: Array.isArray(ids) ? ids : [] };
    const response = await post(`${API_URL}/depots/anexos/download`, payload, {
        responseType: 'blob',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    return response?.data || response;
};
/**
 * Sincroniza anexos por container e numero embarcador.
 * @param {Object} params
 * @param {string} params.container
 * @param {string} params.cpfMotorista
 * @param {File[]} [params.arquivos=[]]
 * @param {string} [params.tag]
 * @returns {Promise<Object>} Resposta da API
 */
export const syncDepotsAnexo = async ({ container, cpfMotorista, arquivos = [], tag, }) => {
    const formData = new FormData();
    const normalizedTag = String(tag || '').toUpperCase();
    const hasCpf = cpfMotorista !== undefined &&
        cpfMotorista !== null &&
        String(cpfMotorista).trim() !== '' &&
        normalizedTag !== 'CTE';
    if (tag) {
        formData.append('TagAnexo', tag);
        formData.append('tag', tag);
    }
    const lista = Array.isArray(arquivos) ? arquivos : [];
    lista.forEach((arquivo) => {
        if (arquivo) {
            formData.append('arquivos', arquivo);
        }
    });
    const baseUrl = `${API_URL}/depots/sync-anexo/${encodeURIComponent(container)}`;
    const url = hasCpf ? `${baseUrl}/${encodeURIComponent(cpfMotorista)}` : baseUrl;
    const response = await post(url, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'x-skip-notification': 'true',
        },
    });
    return response?.data || response;
};
export const sendDepotsEmailClientes = async ({ depotId, clienteId, assunto, anexos = [], tagAnexo = 'RIC', }) => {
    const formData = new FormData();
    formData.append('DepotId', String(depotId));
    formData.append('ClienteId', String(clienteId));
    formData.append('TagAnexo', tagAnexo);
    formData.append('tag', tagAnexo);
    if (assunto !== undefined && assunto !== null && String(assunto).trim() !== '') {
        formData.append('Assunto', String(assunto));
    }
    const arquivos = Array.isArray(anexos) ? anexos : [];
    arquivos.forEach((arquivo) => {
        if (arquivo) {
            formData.append('anexos', arquivo);
        }
    });
    const response = await post(`${API_URL}/depots/email-clientes`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response?.data || response;
};
